import { useNavigate } from '@tanstack/react-router'
import type { Balance, LeaveTypeId } from '@lms/shared-types'
import type { RosterMember } from '../../api/manager-team'
import { displayName } from '../../lib/display-name'
import { fmtDateRange } from '../../lib/format'
import { LEAVE_BALANCE_ORDER } from '../../lib/leave-type-meta'
import { RHYTHM_STYLE } from '../../lib/rhythm-style'
import { humaniseFlagCode, safeLeaveStyle } from '../../lib/roster-format'
import { Modal } from '../primitives/Modal'
import { Icon } from '../primitives/Icon'
import { BalanceTile } from '../employee/PersonalBlock'
import { RhythmAvatar } from './RhythmAvatar'

interface PersonModalProps {
  member: RosterMember | null
  onClose: () => void
}

/**
 * Centered modal popup showing full detail for a roster member. Built on the
 * shared Modal primitive, which provides the overlay, focus trap, inert
 * background, Escape/overlay-click close, reduced-motion handling, and the
 * mobile bottom-sheet fallback — so this component only composes the body
 * sections (balances, recent leaves, flags, replacement).
 */
export function PersonModal({ member, onClose }: PersonModalProps): JSX.Element | null {
  const navigate = useNavigate()
  if (member === null) return null
  const name = displayName(member)

  // Map the roster balances onto the home Balance shape so we can reuse the
  // exact home-page leave tiles (non-interactive — the manager isn't the
  // owner). No carry-forward feature, so carryOver is always 0. The roster
  // now carries the real accrued-so-far cap (below the yearly slice for a
  // first-year annual balance), so the tile can detect the accrual stage and
  // render an overdrawn negative — falling back to entitlement when the field
  // is absent (older payloads), which reads as "not accruing".
  //
  // Unpaid leave has no entitlement cap — the roster returns a 300 sentinel
  // that this quota tile would render as a misleading "300 of 300 left".
  // The employee's own home grid never shows unpaid as a quota either (it's
  // an approval-gated, uncapped type), so drop it here for parity.
  const year = new Date().getFullYear()
  // Map the roster balances onto the home Balance shape, keyed by type, so the
  // modal can render the SAME fixed tile set as the home dashboard — including an
  // empty tile for an applicable type the employee has no balance row for (e.g.
  // Marriage). Unpaid is dropped (uncapped, approval-gated — no quota tile).
  const balanceByType = new Map<LeaveTypeId, Balance>(
    member.balances
      .filter((b) => b.leaveType !== 'unpaid')
      .map((b) => [
        b.leaveType as LeaveTypeId,
        {
          employeeId: member.id,
          leaveType: b.leaveType as LeaveTypeId,
          year,
          entitlement: b.entitlement,
          accruedEntitlement: b.accruedEntitlement ?? b.entitlement,
          used: b.used,
          pending: b.pending,
          carryOver: 0,
          available: b.available,
        },
      ]),
  )
  // Fixed applicable set in the canonical order (leave-type-meta — one source
  // shared with the dashboard): every non-parental quota type always shows (empty
  // when there's no row); the parental type appears only when present (the api
  // already gender-filters the balances, so the applicable one is the one here).
  const displayTypes = LEAVE_BALANCE_ORDER.filter((t) => {
    if (t === 'unpaid') return false
    if (t === 'maternity' || t === 'paternity') return balanceByType.has(t)
    return true
  })

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      title={name}
      // Slightly smaller than the default 20px so the name sits balanced
      // beside the leading avatar rather than towering over it.
      titleSize={18}
      subtitle={member.teamName ?? member.departmentName ?? ''}
      // Same rhythm-coloured avatar shown in the roster row the manager clicked
      // — carries identity continuity from list → detail.
      titleLeading={
        <RhythmAvatar
          name={name}
          band={member.health.band}
          patternCount={member.health.contributingPatterns.length}
          size={40}
        />
      }
      maxWidth={600}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Leave rhythm — a compact, viewer-neutral status. Expands to show
            the active pattern chips only when there is something to flag; no
            oversized "all clear" card — verdict-language-ok — (that hero belongs
            on the employee's own home page, not a manager's read-only drill-in). */}
        <RhythmStatus member={member} />
        {/* Balances — the same leave tiles the employee sees on their home
            page, rendered non-interactive (no /apply link) for the manager. */}
        <section>
          <h3 style={sectionTitleStyle}>Leave balances</h3>
          <div className="balance-tiles" style={{ display: 'grid', gap: 8 }}>
            {displayTypes.map((type) => (
              <BalanceTile
                key={type}
                type={type}
                balance={balanceByType.get(type) ?? null}
                replacementDays={type === 'replacement' ? member.replacementBanked : null}
                isLoading={false}
                interactive={false}
              />
            ))}
          </div>
        </section>

        {/* Recent leaves */}
        <section>
          <h3 style={sectionTitleStyle}>Recent leaves</h3>
          {member.recentLeaves.length === 0 ? (
            <p style={emptyStyle}>No recent leave.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {member.recentLeaves.map((leave, i) => {
                const ls = safeLeaveStyle(leave.leaveType)
                return (
                  <a
                    key={`${leave.id}-${i}`}
                    // Client-side navigation to the leave detail (the /leave
                    // route now declares its `id` search schema, so navigate()
                    // preserves it — no full-page reload / flash). The href is
                    // kept so Cmd/Ctrl/middle-click still open a new tab.
                    href={`/leave?id=${leave.id}`}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
                      e.preventDefault()
                      void navigate({ to: '/leave', search: { id: leave.id } })
                    }}
                    title="View leave detail"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      padding: '6px 8px',
                      margin: '0 -8px',
                      borderRadius: 8,
                      textDecoration: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      transition: 'background var(--motion-duration-base) var(--motion-ease-out)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--surface-2)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: ls.swatch,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: 'var(--text-2)', minWidth: 60 }}>{ls.label}</span>
                    <span style={{ color: 'var(--text-3)', flex: 1 }}>
                      {fmtDateRange(leave.startDate, leave.endDate)}
                    </span>
                    <StatusChip status={leave.status} />
                    <Icon name="chevron_right" size={16} aria-hidden="true" />
                  </a>
                )
              })}
            </div>
          )}
        </section>
      </div>
    </Modal>
  )
}

/**
 * Compact, viewer-neutral leave-rhythm status for the person drill-in: a
 * band-coloured dot + label ("Steady lah" / "Heads up" / "Off rhythm"). When
 * the person has active patterns (health contributingPatterns and/or employee
 * signal flags) it expands to a row of chips; when clear it just says so on one
 * line. Replaces the employee-voiced wellbeing hero card.
 */
function RhythmStatus({ member }: { member: RosterMember }): JSX.Element {
  const style = RHYTHM_STYLE[member.health.band]
  const patternCodes = Array.from(
    new Set([
      ...member.health.contributingPatterns,
      ...member.signals.map((s) => s.flagCode),
    ]),
  )
  const hasPatterns = patternCodes.length > 0
  return (
    <div
      data-testid="rhythm-status"
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <span
          aria-hidden="true"
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: style.ring,
            flexShrink: 0,
          }}
        />
        <span style={{ color: 'var(--text-3)' }}>Leave rhythm:</span>
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{style.label}</span>
        {!hasPatterns ? (
          <span style={{ color: 'var(--text-3)' }}>· no active patterns</span>
        ) : null}
      </span>
      {hasPatterns ? (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {patternCodes.map((code) => (
            <span
              key={code}
              style={{
                fontSize: 11,
                fontWeight: 500,
                padding: '1px 8px',
                borderRadius: 999,
                background: style.chipBg,
                color: style.chipText,
                border: `1px solid ${style.ring}55`,
                whiteSpace: 'nowrap',
              }}
            >
              {humaniseFlagCode(code)}
            </span>
          ))}
        </span>
      ) : null}
    </div>
  )
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--text-3)',
  margin: '0 0 8px',
}

const emptyStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-3)',
  margin: 0,
}

function StatusChip({ status }: { status: string }): JSX.Element {
  const isApproved = status === 'approved'
  const isPending = status === 'pending'
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: 999,
        background: isApproved
          ? 'var(--pear-tint-2)'
          : isPending
          ? 'var(--warning-bg)'
          : 'var(--surface-2)',
        color: isApproved ? 'var(--text-2)' : isPending ? 'var(--warning-fg)' : 'var(--text-3)',
        border: '1px solid',
        borderColor: isApproved
          ? 'var(--pear-tint)'
          : isPending
          ? 'var(--warning-line)'
          : 'var(--border)',
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  )
}
